import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../core/api/api';
import * as commonActions from './action-types';
import authHandler from '../core/api/auth-handler';
import * as appServiceLines from '../core/service-lines';
import { authenticationChannel } from '../core/authentication-channel/authentication-channel';
import * as commonEnums from '../enums/index';
import * as commonActionUtils from './_action-utilities';
import * as cmxDateTime from '../core/date-time/cmx-date-time';

import * as cmxErrors from '../core/errors';

// actions
import { fetchEula } from './eula.actions';
import {
  fetchProcessInstance,
  getCasesWithEncounter,
  getDocumentsAsHtml,
  getFlagItems,
  getCCICustomClaimCrossCases,
  fetchOrderTypeStatus,
  getCodesWithEncounter,
  fetchProviderRoleTypeConfigurations,
  fetchRelateCaseFilterDictionaries
} from './code-bench.actions';
import { loadCase, loadCaseFromURL } from './cases.action';

// util functions
import { parseUCL108 } from '../core/amplify/procedure-parser';
import { loadXCaliber } from './xcaliber.actions';
import { fetchImprovementOptions } from './improvements.actions';
import { isIPC, isPathology, isSurgery, buildCaseDetailUrl } from '../core/service-lines';
import { isResourceAllowed } from '../core/amplify/utils/bench-editable';
import { shimContext } from './_action-utilities';
import { caseEditsActions } from './case-edits.actions';

/**
 * Named navigation targets for the `navigationIntent` action. Kept here so launch flows and
 * listeners share one source of truth; the action creator lives in `navigation-intent.action.ts`.
 */
export type NavigationIntent =
  | 'NOT_AUTHORIZED'
  | 'CASE_NOT_FOUND'
  | 'CASE_CHOOSER'
  | 'EULA'
  | 'SESSION_OPENED'
  | 'CONTEXT_UNAVAILABLE'
  | 'TIMED_OUT'
  | 'CASE_DETAIL';

/**
 * Payload for the `navigationIntent` action. Use the type parameter to describe location `state`
 * when you pass cursor/worklist context, e.g. `NavigationIntentPayload<MyCursorState>`.
 */
export type NavigationIntentPayload<TState = unknown> = {
  intent: NavigationIntent;
  url?: string;
  state?: TState;
  replace?: boolean;
};

import { navigationIntent } from './navigation-intent.action';

export { navigationIntent };

import { relatedCasesSortablePageable } from '../core/amplify/utils/pagination-utils';
import { getRelatedCasesInitialFilters } from '../core/amplify/utils/common-filter-utils';

const { AmplifyAuthenticate, Launch, EpicLaunchPoints } = commonActions;
const { UnauthenticatedError, PageNotFoundError } = cmxErrors;
const { LaunchParams } = commonEnums;

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

// --------------------- sync actions  --------------------- //

const userDetailsAvailable = createAction<CMxAPI.ContextAPI>(
  AmplifyAuthenticate.USER_DETAILS
);
const launchParamsAvailable = createAction<CMxAPI.LaunchParams>(
  Launch.PARAMS_AVAILABLE
);
const contextParams = createAction<any>(Launch.CONTEXT_PARAMS);
const hostCaseId = createAction<{ hostCaseId: number }>(Launch.PARENT_CASE);

const caseSearchAvailable = createAction<CMxAPI.CaseRun[]>(Launch.CASE_SEARCH);

const caseLookupError = createAction<Error>(Launch.CASE_LOOKUP_ERROR);

const billingGroupAvailable = createAction<CMxAPI.PageableList<CMxAPI.Case>>(
  Launch.BILLING_GROUP
);
const caseRunAvailable = createAction<CMxAPI.CaseRun>(Launch.CASE_DETAIL);

const caseRunWithRefreshAvailable = createAction<CMxAPI.CaseRun>(
  Launch.CASE_DETAIL_WITH_REFRESH
);

const epicToken = createAction<string>(Launch.EPIC_TOKEN);

const selectCase = createAsyncThunk<
  CMxAPI.CaseRun,
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.CASE_SELECTED, async (caseRun, { dispatch }) => {
  const { path: slPath, caseDetailVersion } = appServiceLines.get();
  const url = `${slPath}/caseinstance/${caseRun.case_id}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  const withDetails = await api.requestJSON<CMxAPI.Case>(url);
  //
  const activeItem = withDetails.caseRuns[0];
  await loadCase(activeItem, dispatch);
  return activeItem;
});

type PopupLaunchParmas = {
  token: string;
  caseId: string;
  serviceLine: string;
};

const fetchContextJSON = async () => {
  const userDetailsResponse = await api.request('/contexts/v1');
  const contextJSON: CMxAPI.ContextAPI = await userDetailsResponse.json();
  return contextJSON;
};

const tokenAvailable = createAsyncThunk<
  void,
  PopupLaunchParmas,
  { rejectValue: CMxCommonApp.CMxError }
>(
  Launch.LOAD_CASE_WITH_TOKEN,
  async (launchParams: PopupLaunchParmas, { dispatch }) => {
    authHandler.token = launchParams.token;
    const sl = appServiceLines.determineServiceLineConfig({
      name: launchParams.serviceLine,
      caseIdentifier: `${launchParams.caseId}`
    });
    appServiceLines.set(sl);

    const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();

    dispatch(userDetailsAvailable(contextJSON));

    const { path: slPath, caseDetailVersion } = appServiceLines.get();
    const url = `${slPath}/caseinstance/${launchParams.caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
    const withDetails = await api.requestJSON<CMxAPI.Case>(url);
    //
    const activeItem = withDetails.caseRuns[0];
    const caseUrl = (await loadCase(activeItem, dispatch)).url;

    dispatch(caseRunAvailable(activeItem));
    dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: caseUrl }));
    return;
  }
);

const loadCaseFromId = createAsyncThunk<
  void,
  Pick<PopupLaunchParmas, 'caseId'>,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.LOAD_CASE_FROM_ID, async (launchParams, { dispatch }) => {
  const { path: slPath, caseDetailVersion } = appServiceLines.get();

  const url = `${slPath}/caseinstance/${launchParams.caseId}/details/${caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;
  const withDetails = await api.requestJSON<CMxAPI.Case>(url);
  //
  const activeItem = withDetails.caseRuns[0];
  await loadCase(activeItem, dispatch);
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line,
    caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
  });

  const caseId =
    serviceLineConfig.caseIdProp in activeItem
      ? activeItem[serviceLineConfig.caseIdProp]
      : activeItem.case_id;

  const sl = activeItem.service_line?.toLowerCase() ?? '';
  dispatch(caseRunAvailable(activeItem));
  dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: `/${sl}/case/${caseId}/case-run/${activeItem.process_instance_id}` }));
  return;
});

export const loadCaseFromUCLs = async (resultSet: any) => {
  if (resultSet.tracking) {
    for (let i = 0; i < resultSet.tracking.length; i++) {
      const procedureInfo = resultSet.tracking[i];
      try {
        const caseDetail = await api.requestJSON<CMxAPI.CaseRun>(
          `${procedureInfo.serviceLine.path}/processinstance/${procedureInfo.identifier}/details/v1`
        );
        if (caseDetail) {
          appServiceLines.set(procedureInfo.serviceLine);
          return caseDetail;
        }
      } catch (e) {
        /** squelch */
      }
    }
  }

  if (resultSet.standard) {
    for (let i = 0; i < resultSet.standard.length; i++) {
      const procedureInfo = resultSet.standard[i];
      let caseDetail;

      try {
        // radiology
        if (procedureInfo.serviceLine.path === '') {
          caseDetail = await api.requestJSON<CMxAPI.CaseRun>(
            `${procedureInfo.serviceLine.path}/caseinstance/procedureid/${procedureInfo.identifier}/v1`
          );
        } else {
          caseDetail = await api.requestJSON<CMxAPI.CaseRun>(
            `${procedureInfo.serviceLine.path}/caseinstance/transactionid/${procedureInfo.identifier}/v1`
          );
        }
        if (caseDetail) {
          appServiceLines.set(procedureInfo.serviceLine);
          return caseDetail;
        }
      } catch (e) {
        /** squelch */
      }
    }
  }

  throw new Error(`unable to locate a case from parsed ucl108 ids.`);
};

const fetchCases = async (
  externalApp: CMxAPI.LaunchParams,
  dispatch: any
) => {
  let caseJson;

  try {
    const launchParams = new URLSearchParams(externalApp);
    const hasUCL108 = launchParams.has(LaunchParams.TRANSACTION_IDS);

    if (hasUCL108 && launchParams.get(LaunchParams.TRANSACTION_IDS) !== '') {
      // Lookup flow:
      // 1. parse UCL108
      // 2. attempt UCL108 search
      // 3. if no case found, do a context search
      const ucl108List = launchParams.get(LaunchParams.TRANSACTION_IDS);
      try {
        // 1. parse UCL108
        const results = parseUCL108(ucl108List);
        // 2. attempt UCL108 search
        const caseRun = await loadCaseFromUCLs(results);

        if (caseRun) {
          const fetchResult = await loadCase(caseRun, dispatch);
          dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: fetchResult.url }));
        }
      } catch (e) {
        // 3. if no case found, do a context search
        await runConxtextSearch(launchParams, dispatch);
      }
    } else if (launchParams.get(LaunchParams.PATIENT_ID)) {
      await runConxtextSearch(launchParams, dispatch);
    } else {
      throw new Error(`Not enough information to lookup a case`);
    }
  } catch (e) {
    dispatch(navigationIntent({ intent: 'CASE_NOT_FOUND' }));
    dispatch(caseLookupError(e));
  }
  return caseJson;
};

const runConxtextSearch = async (
  searchParams: URLSearchParams,
  dispatch: any
) => {
  const searchResults = await backupCaseSearch(searchParams);
  const resultCount = searchResults?.length ?? 0;
  if (resultCount === 1) {
    const [caseRun] = searchResults;
    const contextResult = await loadCase(caseRun, dispatch);
    dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: contextResult.url }));
  } else if (resultCount > 1) {
    dispatch(caseSearchAvailable(searchResults));
    dispatch(navigationIntent({ intent: 'CASE_CHOOSER' }));
  } else {
    dispatch(navigationIntent({ intent: 'CASE_NOT_FOUND' }));
  }
};
const backupCaseSearch = async (searchParams: URLSearchParams) => {
  const options = {
    method: 'POST',
    body: JSON.stringify({
      patientMrn: `${searchParams.get(LaunchParams.PATIENT_ID)}`,
      dateOfService: `${searchParams.get(LaunchParams.VISIT_DATE)}`,
      providerNumber: `${searchParams.get(LaunchParams.PROVIDER_ID)}`,
      visitNumber: `${searchParams.get(LaunchParams.VISIT_CSN)}`
    })
  };
  const searchResults = await api.requestJSON<CMxAPI.CaseRun[]>(
    `${appServiceLines.get().path}/caseinstance/listbycontext/v1`,
    options
  );
  return searchResults;
};

const embeddedLaunch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  URLSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(AmplifyAuthenticate.AUTHENTICATE, async (authRequest, { dispatch }) => {
  if (!authRequest.has('data') || !authRequest.has('appId')) {
    dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    throw new Error(`missing request data`);
  }

  // ------------ launch point ------------
  const appId = encodeURIComponent(authRequest.get('appId'));
  const data = encodeURIComponent(authRequest.get('data'));
  const searchParams = `appId=${appId}&data=${data}`;
  let launchRequest;

  // ------------ authentication ------------
  try {
    launchRequest = await api.request(
      '/externalApplication/authenticate/v1' + `?` + searchParams,
      { ...REQUEST_INIT_OPTIONS }
    );
  } catch (e) {
    dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    throw new UnauthenticatedError(`unauthenticated`);
  }

  // ------------ launch parameters ------------
  const launchParams = await launchRequest.json();
  dispatch(launchParamsAvailable(launchParams));

  const userDetailsResponse = await api.request('/contexts/v1');
  const contextJSON: CMxAPI.ContextAPI = await userDetailsResponse.json();

  // ------------ eula check ------------
  const location = userDetailsResponse.headers.get('location');
  const eulaLocation = '/eula/';
  if (location === eulaLocation) {
    dispatch(fetchEula(contextJSON));
    dispatch(navigationIntent({ intent: 'EULA' }));
  }
  dispatch(userDetailsAvailable(contextJSON));

  // ------------ launch point ------------
  // The launchPoint may come through as a quoted string.
  // We strip the string of any quotation marks
  const launchPoint = `${launchParams.launchPoint}`.replace(/[\"\']/g, '');

  if (launchPoint === EpicLaunchPoints.ERROR) {
    dispatch(navigationIntent({ intent: 'CONTEXT_UNAVAILABLE' }));
    throw new PageNotFoundError('Unable to determine Context');
  } else if (launchPoint === EpicLaunchPoints.ERROR2) {
    dispatch(navigationIntent({ intent: 'SESSION_OPENED' }));
    throw new PageNotFoundError('Session already opened');
  }

  // ------------ case lookup ------------
  if (location !== eulaLocation) {
    const caseDetails = await fetchCases(launchParams, dispatch);
    // ------------ xcaliber token fetch ------------
    await loadXCaliber(dispatch);
    return caseDetails;
  } else {
    return null;
  }
});

type SimpleParams = {
  params: {
    [key: string]: string;
  };
};

/**
 * Method to load case details with refresh as true
 * This grabs latest data from XC
 * @param url
 * @param processInstanceIdProp
 * @param match
 * @param dispatch
 * @returns
 */
const loadCaseRunWithRefresh = async (
  url: string,
  processInstanceIdProp: string,
  match: SimpleParams,
  dispatch: any
) => {
  if (!url) return;
  const urlWithRefresh = `${url}&refresh=true`;
  const withRefreshDetails = await api.requestJSON<CMxAPI.Case>(
    urlWithRefresh,
    {},
    true
  );
  let activeItemWithRefresh = withRefreshDetails.caseRuns.find(
    caseRun =>
      caseRun[processInstanceIdProp].toString() === match.params.id.toString()
  );
  if (!activeItemWithRefresh) {
    activeItemWithRefresh = withRefreshDetails.caseRuns[0];
  }
  dispatch(caseRunWithRefreshAvailable(activeItemWithRefresh));
};

const loadFromURLMatch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  SimpleParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Launch.LOAD_FROM_URL, async (match, { dispatch }) => {
  const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();
  const shimmedContext = shimContext(contextJSON);
  dispatch(userDetailsAvailable(contextJSON));

  // Check feature flag for display_all_case_runs
  const useV3Endpoint = shimmedContext.activeContext
    ? isResourceAllowed(
        shimmedContext.activeContext,
        commonEnums.ResourceId.DISPLAY_ALL_CASE_RUNS,
        match.params.serviceLine
      )
    : false;

  const sl = appServiceLines.determineServiceLineConfig({
    name: match.params.serviceLine,
    displayAllCaseRuns: useV3Endpoint
  });

  const processInstanceIdProp = appServiceLines.determineProcessInstanceId({
    name: match.params.serviceLine
  });

  // Use utility function to build URL
  const url = buildCaseDetailUrl(
    match.params.caseId,
    sl
  );

  const withDetails = await api.requestJSON<CMxAPI.Case>(url, {}, true);

  // Use v3 logic for pathology
  let activeItem = withDetails.caseRuns.find(
    caseRun =>
      caseRun[processInstanceIdProp].toString() === match.params.id.toString()
  );

  if (!activeItem) {
    activeItem = withDetails.caseRuns[0];
  }

  if (isIPC(activeItem.service_line)) {
    await dispatch(getDocumentsAsHtml(activeItem));
  }

  await loadCaseFromURL(activeItem, dispatch);

  if (!!withDetails && match.params?.getCaserunWithRefresh) {
    try {
      loadCaseRunWithRefresh(url, processInstanceIdProp, match, dispatch);
    } catch (error) {
      console.log(
        'Error while trying to load case run with refresh ',
        error?.message
      );
    }
  }

  let processInstance;
  try {
    processInstance = await dispatch(fetchProcessInstance(activeItem)).unwrap();
  } catch (e) {
    // ignore
  }

  // mock for tests
  if (!processInstance) {
    // do nothing
  }

  if (isIPC(activeItem.service_line) || isSurgery(activeItem.service_line)) {
    dispatch(
      caseEditsActions.loadAllPatientDetailOptions({
        activeTenantId: activeItem.tenant_uid,
        facility: activeItem.facility
      })
    );
  }
  // ------------ xcaliber token fetch ------------
  await loadXCaliber(dispatch);

  try {
    if (isIPC(activeItem?.service_line)) {
      await dispatch(getFlagItems(activeItem as CMxAPI.CaseRunV2));
    }
  } catch (error) {
    console.log(`Error while fetching flags: ${error.message}`);
  }
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: activeItem.service_line,
    caseIdentifier: `${activeItem.case_uid || activeItem.case_id}`
  });

  try {
    await dispatch(
      fetchImprovementOptions({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );
  } catch (error) {
    console.log(`Error while fetching improvement options: ${error.message}`);
  }

  try {
    await dispatch(
      fetchProviderRoleTypeConfigurations({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );
  } catch (error) {
    console.log(
      `Error while fetching provider role type configuration: ${error.message}`
    );
  }

  try {
    if (!isPathology(activeItem?.service_line)) {
      dispatch(
        getCCICustomClaimCrossCases({
          activeItem: activeItem,
          processInstance: processInstance
        })
      );
    }

    dispatch(
      fetchOrderTypeStatus({
        activeItem: activeItem,
        processInstance: processInstance
      })
    );

    let filters;
    try {
      filters = await dispatch(
        fetchRelateCaseFilterDictionaries({
          activeItem: activeItem,
          processInstance: processInstance
        })
      ).unwrap();
    } catch (e) {
      // ignore
    }
    let startDate: Date;
    let endDate: Date;
    if (activeItem.admission_datetime && activeItem.discharge_datetime) {
      startDate = activeItem.admission_datetime
      endDate = activeItem.discharge_datetime
    } else {
      startDate = cmxDateTime.format(
        new Date(),
        cmxDateTime.FORMATS.DATE_TIME_WITH_ZONE,
        {
          throw: false,
          localized: true,
          offset: {
            action: cmxDateTime.DateManipulateOperation.SUBTRACT,
            delta: [30, cmxDateTime.DateManipulateUnit.DAY]
          }
        }
      )
      endDate = new Date()
    }


    const filterSelection = getRelatedCasesInitialFilters(
      filters,
      startDate,
      endDate
    );

    if (activeItem?.encounter_identifier?.number) {
      // Currently Pathology does not support this method of related cases, therefore skip if Path
      // Added for AUTO-6674
      if (!isPathology(activeItem.service_line)) {
        dispatch(
          getCasesWithEncounter({
            encounterId: activeItem?.encounter_identifier?.number,
            sortablePageable: { ...relatedCasesSortablePageable },
            serviceLine: serviceLineConfig.amplifyPath,
            filterSelection
          })
        );
      }
      dispatch(
        getCodesWithEncounter({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'ICD',
          caseId: activeItem.case_id
        })
      );
      dispatch(
        getCodesWithEncounter({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'CPT',
          caseId: activeItem.case_id
        })
      );
      dispatch(
        getCodesWithEncounter({
          encounterId: activeItem?.encounter_identifier?.number,
          serviceLine: serviceLineConfig.amplifyPath,
          codeType: 'MODIFIER',
          caseId: activeItem.case_id
        })
      );
    }
  } catch (error) {
    console.log(`Error while fetching related cases: ${error.message}`);
  }
  return withDetails;
});

const standaloneLaunch = createAsyncThunk<
  CMxAPI.Case | CMxAPI.Case[],
  URLSearchParams,
  { rejectValue: CMxCommonApp.CMxError }
>(AmplifyAuthenticate.AUTOMATE, async (searchParams, { dispatch }) => {
  if (searchParams.has('channel')) {
    try {
      await authenticationChannel.ready;

      const params = {};
      // Context params can be passed in via a queryString
      [...searchParams.keys()].forEach((key: string) => {
        let value: any;

        // special case for processInstanceGuids which are an
        // array.
        if (key === 'processInstanceGuids') {
          value = searchParams.get(key).split(',');
        } else {
          value = searchParams.get(key);
        }
        params[key] = value;
      });
      dispatch(contextParams(params));

      const sl = appServiceLines.determineServiceLineConfig({
        name: searchParams.get('serviceLine'),
        caseIdentifier: `${searchParams.get('caseId')}`
      });
      appServiceLines.set(sl);

      const url = `${sl.path}/caseinstance/${searchParams.get(
        'caseId'
      )}/details/${
        sl.caseDetailVersion
      }?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;

      const withDetails = await api.requestJSON<CMxAPI.Case>(url);
      //
      const processInstanceGuid = searchParams.get('processInstanceGuid');
      let activeItem = withDetails.caseRuns.find(
        caseRun => caseRun.process_instance_guid === processInstanceGuid
      );
      if (!activeItem) {
        activeItem = withDetails.caseRuns[0];
      }

      const contextJSON: CMxAPI.ContextAPI = await fetchContextJSON();
      dispatch(userDetailsAvailable(contextJSON));

      const useGuid = searchParams.get('useGuid') ?? 'false';
      const caseLoadResult = await loadCase(activeItem, dispatch, useGuid);
      dispatch(navigationIntent({ intent: 'CASE_DETAIL', url: caseLoadResult.url }));
      await loadXCaliber(dispatch);

      return withDetails;
    } catch (e) {
      dispatch(navigationIntent({ intent: 'NOT_AUTHORIZED' }));
    }
  }
});

export {
  epicToken,
  loadFromURLMatch,
  standaloneLaunch,
  hostCaseId,
  loadCaseFromId,
  tokenAvailable,
  contextParams,
  launchParamsAvailable,
  selectCase,
  embeddedLaunch,
  userDetailsAvailable,
  caseRunAvailable,
  caseRunWithRefreshAvailable,
  caseSearchAvailable,
  caseLookupError,
  billingGroupAvailable,
  fetchCases
};
